// @flow
/* eslint jsx-a11y/label-has-for:0 */

import React, { Component, Fragment } from 'react';
import i18n from 'app/common/lib/i18n';
import Modal from 'app/common/react/ui/Modal';
import { interpolateNamedTemplate } from 'app/common/lib/react/interpolate';
import { Box, Button, Checkbox, Divider, Label, Modal as GestaltModal, Text } from 'gestalt';

type RemoveType = 'declineRequest' | 'removeCollaborator';

type removeSelfProps = {
  onCancel: () => void,
  onRemove: () => void,
  // NOTE(allenk): Only using this prop temporarily as a workaround for the web_board_collaborator_flyout experiment
  useGestaltModal?: boolean,
};
export function RemoveSelfFromBoard(props: removeSelfProps) {
  const { onRemove } = props;
  const message = i18n._(
    'Are you sure you want to leave this group board and remove it from your profile?',
  );
  return (
    <CollaboratorButtonForm
      {...props}
      onConfirm={onRemove}
      submitMessage={i18n._('Leave')}
      title={i18n._('Leave board?')}
    >
      <Box padding={5}>
        <Text>{message}</Text>
      </Box>
    </CollaboratorButtonForm>
  );
}

export function RemoveSelfFromBoardModal(props: removeSelfProps) {
  const { onCancel, onRemove, useGestaltModal } = props;
  const message = i18n._(
    'Are you sure you want to leave this group board and remove it from your profile?',
  );

  const ModalComponent = useGestaltModal ? GestaltModal : Modal;

  return (
    <ModalComponent
      accessibilityCloseLabel={i18n._('close', 'accessible label for button to close modal')}
      accessibilityModalLabel={i18n._('Leave board?')}
      heading={i18n._('Leave board?')}
      onDismiss={onCancel}
      size="sm"
      footer={
        <Box display="flex" justifyContent="end">
          <Box paddingX={1}>
            <Button inline onClick={onCancel} text={i18n._('Cancel')} />
          </Box>
          <Box paddingX={1}>
            <Button color="red" inline onClick={onRemove} text={i18n._('Leave')} />
          </Box>
        </Box>
      }
    >
      <Box padding={5}>
        <Text>{message}</Text>
      </Box>
    </ModalComponent>
  );
}

export class RemoveOtherFromBoard extends Component<
  {
    removeType: RemoveType,
    firstName: ?string,
    fullName: string,
    onCancel: () => void,
    onRemove: () => void,
    onRemoveAndBlock: () => void,
    asModal: boolean,
    useGestaltModal?: boolean,
  },
  { shouldBlock: boolean },
> {
  static defaultProps = {
    asModal: false,
  };
  state: { shouldBlock: boolean } = { shouldBlock: false };

  getBlockDescription(removeType: RemoveType, firstName: ?string) {
    if (removeType === 'declineRequest') {
      if (firstName) {
        return interpolateNamedTemplate(
          i18n._(
            'This will decline {{ invited_user_first_name }} from joining this board ' +
              'and block them from subsequent collaboration requests.',
            "decline another pinner's request to join board as a collaborator and block them",
          ),
          { invited_user_first_name: firstName },
        );
      } else {
        return i18n._(
          'This will decline this person from joining this board and block them from subsequent collaboration requests.',
          'message that explains consequences of declining a request to join board as a collaborator',
        );
      }
    } else if (firstName) {
      return interpolateNamedTemplate(
        i18n._(
          'This will remove {{ invited_user_first_name }}, ' +
            'people {{ invited_user_first_name }} invited, and all their Pins.',
        ),
        { invited_user_first_name: firstName },
      );
    } else {
      return i18n._(
        'This will remove this person, people they invited, and all their Pins.',
        'message that explains consequences of removing a collaborator',
      );
    }
  }

  render() {
    const {
      asModal,
      removeType,
      firstName,
      fullName,
      onCancel,
      onRemove,
      onRemoveAndBlock,
      useGestaltModal,
    } = this.props;
    const title =
      removeType === 'declineRequest'
        ? interpolateNamedTemplate(
            i18n._(
              'Decline {{ invited_user_name }}?',
              "[button] to decline a pinner's request to join a board as a collaborator",
            ),
            {
              invited_user_name: fullName,
            },
          ).join('')
        : interpolateNamedTemplate(
            i18n._(
              'Remove {{ invited_user_name }}?',
              '[button] to remove a pinner as a collaborator from a group board',
            ),
            {
              invited_user_name: fullName,
            },
          ).join('');
    const message = i18n._(
      'This will keep them from saving Pins to this board. You can also:',
      "decline another pinner's request to join board as collaborator. Ending fragment preceeds checkbox for blocking the user.",
    );

    const blockDescription = this.getBlockDescription(removeType, firstName);

    const submitButtonText = this.state.shouldBlock
      ? i18n._(
          'Block',
          'block another pinner after denying their request to collaborate on a board',
        )
      : i18n._('Remove', "decline another pinner's request to collaborate on a board");

    const bodyMarginX = asModal ? 0 : -1;
    const body = (
      <Fragment>
        <Box padding={5}>
          <Text>{message}</Text>
        </Box>
        <Divider />
        <Box padding={5} display="flex" marginLeft={bodyMarginX} marginRight={bodyMarginX}>
          <Box paddingX={1}>
            <Checkbox
              checked={this.state.shouldBlock}
              id="banUser"
              name="ban"
              onChange={() => this.setState({ shouldBlock: !this.state.shouldBlock })}
            />
          </Box>
          <Box paddingX={1}>
            <Label htmlFor="banUser">
              <Text bold>
                {interpolateNamedTemplate(i18n._('Block {{ name }}'), { name: firstName || '' })}
              </Text>
              <Text size="sm">{blockDescription}</Text>
            </Label>
          </Box>
        </Box>
      </Fragment>
    );

    if (asModal) {
      const ModalComponent = useGestaltModal ? GestaltModal : Modal;

      return (
        <ModalComponent
          accessibilityCloseLabel={i18n._('close', 'accessible label for button to close modal')}
          accessibilityModalLabel={title}
          heading={title}
          onDismiss={onCancel}
          size="sm"
          footer={
            <Box display="flex" justifyContent="end" marginLeft={-1} marginRight={-1}>
              <Box paddingX={1}>
                <Button inline onClick={onCancel} text={i18n._('Cancel')} />
              </Box>
              <Box paddingX={1}>
                <Button
                  color="red"
                  inline
                  onClick={this.state.shouldBlock ? onRemoveAndBlock : onRemove}
                  text={submitButtonText}
                />
              </Box>
            </Box>
          }
        >
          {body}
        </ModalComponent>
      );
    } else {
      return (
        <CollaboratorButtonForm
          {...this.props}
          onConfirm={this.state.shouldBlock ? onRemoveAndBlock : onRemove}
          submitMessage={submitButtonText}
          title={title}
        >
          {body}
        </CollaboratorButtonForm>
      );
    }
  }
}

type formProps = {
  title: string,
  children?: any,
  onCancel: () => void,
  onConfirm: () => void,
  submitMessage: string,
};

function CollaboratorButtonForm(props: formProps) {
  return (
    <div className="ConfirmDialog">
      <form className="standardForm" method="post">
        <h1>{props.title}</h1>
        <div>{props.children}</div>
        <div className="formFooter">
          <Box display="flex" justifyContent="end" marginLeft={-1} marginRight={-1}>
            <Box paddingX={1}>
              <Button inline onClick={props.onCancel} text={i18n._('Cancel')} />
            </Box>
            <Box paddingX={1}>
              <Button
                color="red"
                inline
                type="submit"
                onClick={props.onConfirm}
                text={props.submitMessage}
              />
            </Box>
          </Box>
        </div>
      </form>
    </div>
  );
}
